import { SectionOptionSchema } from '@/entities/templates/config/types';

import { AI_SLOT_PREFIX } from '../config';

export const isAiSlotName = (slotName: string): boolean =>
  slotName.startsWith(AI_SLOT_PREFIX);

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

type ToggleAiSlotNameArgs = {
  slotName: string;
  occupiedNames: Set<string>;
};

export const toggleAiSlotName = ({
  slotName,
  occupiedNames,
}: ToggleAiSlotNameArgs): string => {
  const toAi = !isAiSlotName(slotName);
  const candidate = toAi
    ? `${AI_SLOT_PREFIX}${slotName}`
    : slotName.slice(AI_SLOT_PREFIX.length);

  if (candidate.length > 0 && !occupiedNames.has(candidate)) {
    return candidate;
  }

  let nextNumber = 1;
  while (
    occupiedNames.has(
      toAi ? `${AI_SLOT_PREFIX}${nextNumber}` : String(nextNumber),
    )
  ) {
    nextNumber++;
  }

  return toAi ? `${AI_SLOT_PREFIX}${nextNumber}` : String(nextNumber);
};

type RenameSlotInOptionArgs = {
  option: SectionOptionSchema;
  oldName: string;
  newName: string;
};

export const renameSlotInOption = ({
  option,
  oldName,
  newName,
}: RenameSlotInOptionArgs): SectionOptionSchema => {
  // nosemgrep: javascript.lang.security.audit.detect-non-literal-regexp.detect-non-literal-regexp
  const tokenRegex = new RegExp(`\\{\\{${escapeRegExp(oldName)}\\}\\}`, 'g');
  const text = option.text.replace(tokenRegex, `{{${newName}}}`);

  const { [oldName]: oldValues, ...restSlots } = option.slots ?? {};
  const slots = {
    ...restSlots,
    [newName]: isAiSlotName(newName) ? [] : oldValues ?? [],
  };

  const { [oldName]: movedInstructions, ...restInstructions } =
    option.slot_instructions ?? {};
  const slotInstructions = movedInstructions
    ? { ...restInstructions, [newName]: movedInstructions }
    : restInstructions;

  return {
    ...option,
    text,
    slots,
    slot_instructions:
      Object.keys(slotInstructions).length > 0 ? slotInstructions : undefined,
  };
};
